import React from 'react';
import { View, TouchableOpacity, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { ITableRowProps, IColumnConfig } from '@/types/admin/dataTable';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { SelectionIndicator } from '@/components/common/admin/mobile/SelectionIndicator';
import { useGlassDataTableDensity } from '@/components/admin/redesign/GlassDataTable';

export const TableRow = <T,>({
  row,
  columns,
  index,
  onPress,
  isTabletView = false,
  mobileColumnCount = 2,
  isSelected = false,
  enableSelection = false,
}: ITableRowProps<T>) => {
  const styles = useStyles();
  const { cellPaddingY } = useGlassDataTableDensity();

  const renderCellValue = (column: IColumnConfig<T>, value: any) => {
    if (!isTabletView && column.renderMobileCell) {
      return column.renderMobileCell(value, row, index);
    }

    if (column.renderCell) {
      return column.renderCell(value, row, index);
    }

    if (value === null || value === undefined) {
      return <Text style={styles.cellText}>N/A</Text>;
    }

    if (typeof value === 'boolean') {
      return <Text style={styles.cellText}>{value ? 'Yes' : 'No'}</Text>;
    }

    if (value instanceof Date) {
      return (
        <Text style={styles.cellText}>
          {value.toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </Text>
      );
    }

    return (
      <Text style={styles.cellText} numberOfLines={3}>
        {String(value)}
      </Text>
    );
  };

  const visibleColumns = columns.filter((column) => {
    if (isTabletView) {
      return column.visible !== false;
    }
    return column.mobileVisible !== false;
  });

  const mobileRows: IColumnConfig<T>[][] = [];
  if (!isTabletView) {
    for (let i = 0; i < visibleColumns.length; i += mobileColumnCount) {
      mobileRows.push(visibleColumns.slice(i, i + mobileColumnCount));
    }
  }

  const renderMobileGrid = () =>
    mobileRows.map((rowColumns, rowIndex) => (
      <View key={rowIndex} style={styles.mobileRow}>
        {rowColumns.map((column, colIndex) => (
          <View
            key={String(column.key)}
            style={[styles.mobileCell, colIndex === 0 ? styles.mobileCellLeft : styles.mobileCellRight]}
          >
            {renderCellValue(column, row[column.key as keyof T])}
          </View>
        ))}
        {rowColumns.length < mobileColumnCount &&
          Array.from({ length: mobileColumnCount - rowColumns.length }).map((_, emptyIndex) => (
            <View key={`empty-${emptyIndex}`} style={[styles.mobileCell, styles.mobileCellRight]} />
          ))}
      </View>
    ));

  const renderTabletGrid = () => {
    const primaryColumns = visibleColumns.slice(0, 2);
    const secondaryColumns = visibleColumns.slice(2);

    return (
      <>
        <View style={styles.topRow}>
          {primaryColumns.map((column, colIndex) => (
            <View
              key={String(column.key)}
              style={[styles.primaryCell, colIndex === 0 ? styles.primaryCellLeft : styles.primaryCellRight]}
            >
              {renderCellValue(column, row[column.key as keyof T])}
            </View>
          ))}
        </View>

        {secondaryColumns.length > 0 && (
          <View style={styles.additionalInfo}>
            {secondaryColumns.map((column) => (
              <View key={String(column.key)} style={styles.infoRow}>
                <Text style={styles.infoLabel}>{column.label}:</Text>
                <View style={styles.infoValue}>
                  {renderCellValue(column, row[column.key as keyof T])}
                </View>
              </View>
            ))}
          </View>
        )}
      </>
    );
  };

  return (
    <TouchableOpacity
      style={[
        styles.container,
        { paddingVertical: cellPaddingY },
        isSelected && styles.selectedContainer,
      ]}
      onPress={() => onPress?.(row, index)}
      activeOpacity={0.7}
    >
      {enableSelection && <SelectionIndicator selected={isSelected} size={20} />}

      <View style={styles.mainContent}>
        {isTabletView ? renderTabletGrid() : renderMobileGrid()}
      </View>
    </TouchableOpacity>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors }) => {
    const admin = components.admin;
    return StyleSheet.create({
      container: {
        backgroundColor: 'transparent',
        borderBottomWidth: 1,
        borderBottomColor: admin.dataTable.border,
        paddingHorizontal: 12,
        paddingVertical: 12,
        flexDirection: 'row',
        alignItems: 'center',
      },
      selectedContainer: {
        backgroundColor: admin.selector.itemSelectedBg,
      },
      mainContent: {
        flex: 1,
      },
      mobileRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        marginBottom: 6,
      },
      mobileCell: {
        flex: 1,
        paddingHorizontal: 4,
      },
      mobileCellLeft: {
        alignItems: 'flex-start',
      },
      mobileCellRight: {
        alignItems: 'flex-end',
      },
      topRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: 8,
      },
      primaryCell: {
        flex: 1,
      },
      primaryCellLeft: {
        marginRight: 12,
      },
      primaryCellRight: {
        alignItems: 'flex-end',
      },
      cellText: {
        fontSize: 14,
        color: admin.dataTable.text,
        flexWrap: 'wrap',
      },
      additionalInfo: {
        marginTop: 12,
        paddingTop: 12,
        borderTopWidth: 1,
        borderTopColor: admin.dataTable.border,
      },
      infoRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 4,
      },
      infoLabel: {
        fontSize: 12,
        color: colors.text.dim,
        fontWeight: '500',
      },
      infoValue: {
        flex: 1,
        alignItems: 'flex-end',
      },
    });
  });
